import {Box} from "@mui/material";
import {PropsWithChildren, useCallback, useRef, useState} from "react";
import {useWebchat} from "~/components/webchat/useWebchat";
import {NavBar} from "./NavBar";
import {Drawer} from "./Drawer";
import {Footer} from "./Footer";
import {SideMenu} from "./SideMenu";
import {Container} from "../components/Container";
import {GradientBackground, slideDown, easeOutSmooth} from "../components/animations";
import {colors, layout, spacing} from "../theme";
import {setDrawerHeight, useDrawerHeight} from "../hooks/useDrawerHeight";
import {NewDesignProvider, useNewDesignContext} from "./NewDesignLayoutContext";
import {useAccount, useQuestionnaireVariant} from "../hooks";
import {useGetQuestionnaireQuery} from "~/api";

const NewDesignLayoutInner = ({children}: PropsWithChildren) => {
  const {accountId} = useAccount();
  const isQuestionnaire = useQuestionnaireVariant();
  useGetQuestionnaireQuery({accountId}, {enabled: !!accountId && isQuestionnaire});

  const [menuOpen, setMenuOpen] = useState(false);
  const drawerHeight = useDrawerHeight();
  const {layoutConfig, drawerConfig} = useNewDesignContext();

  const observerRef = useRef<ResizeObserver | null>(null);
  const drawerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) {
      setDrawerHeight(0);
      return;
    }
    const update = () => setDrawerHeight(node.offsetHeight);
    update();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(node);
  }, []);

  const {openChat} = useWebchat();

  const {
    backgroundColor = colors.tertiary[200],
    background,
    gradientBg = false,
    hideNavbar = false,
    hideFooter = false,
    fullPage = false,
    floatingCta = false,
  } = layoutConfig;

  return (
    <Box
      sx={{
        minHeight: "100svh",
        display: "flex",
        justifyContent: "center",
        position: "relative",
        ...(background ? {background} : {backgroundColor}),
      }}
    >
      {gradientBg && <GradientBackground fadeToColor={backgroundColor} />}
      <Box
        sx={{
          width: "100%",
          maxWidth: layout.maxWidth,
          display: "flex",
          flexDirection: "column",
          minHeight: "100svh",
          position: "relative",
          zIndex: 1,
        }}
      >
        {!hideNavbar && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 20,
              display: "flex",
              justifyContent: "center",
              opacity: 0,
              animation: `${slideDown} 0.45s ${easeOutSmooth} forwards`,
              transform: "translateZ(0)",
            }}
          >
            <Box sx={{width: "100%", maxWidth: layout.maxWidth}}>
              <NavBar onChatClick={() => openChat({})} onMenuClick={() => setMenuOpen(true)} />
            </Box>
          </Box>
        )}
        <Container
          gap={0}
          sx={{
            flex: 1,
            position: "relative",
            zIndex: 1,
            ...(drawerConfig && drawerHeight > 0 && {pb: `${drawerHeight - 24}px`}),
          }}
        >
          {fullPage ? (
            children
          ) : (
            <Container gap={0} sx={{pt: `${layout.contentTop}px`, px: spacing[6], minHeight: "100svh"}}>
              {children}
            </Container>
          )}
          {!hideFooter && <Footer sx={floatingCta ? {paddingBottom: `${layout.floatingCtaSpace}px`} : undefined} />}
        </Container>
        {drawerConfig && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              transform: "translateZ(0)",
            }}
          >
            <Box ref={drawerRef} sx={{width: "100vw"}}>
              <Drawer {...drawerConfig} />
            </Box>
          </Box>
        )}
      </Box>
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </Box>
  );
};

export const NewDesignLayout = ({children}: PropsWithChildren) => (
  <NewDesignProvider>
    <NewDesignLayoutInner>{children}</NewDesignLayoutInner>
  </NewDesignProvider>
);
